// OutsourceInvoiceDetail.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  FaArrowLeft,
  FaDownload,
  FaPrint,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaPlus
} from 'react-icons/fa';

import Footer from '../../../footer.js';
import { Button, Spinner, Table } from 'react-bootstrap';
import Header from '../../../header/header.js';
import Navvbar from '../../../header/Navvbar.js';
import axios from 'axios';
import jsPDF from 'jspdf';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import { useCurrency } from '../../../../context/currencyContext.js';

import './OutsourceinvoiceDetails.css';

const OutsourceInvoiceDetail = () => {
  const { currency, formatAmount } = useCurrency();
  const { invoiceNumber } = useParams();
  const navigate = useNavigate();

  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [currentUserLabId, setCurrentUserLabId] = useState(null);

  const [isSourceLab, setIsSourceLab] = useState(false);
  const [isDestinationLab, setIsDestinationLab] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);

  const [editFormData, setEditFormData] = useState({
    invoiceStatus: 'unpaid',
    invoiceMessage: '',
    subtotal: 0,
    taxes: 0,
    total: 0,
    invoiceLines: []
  });

  const [savingEdit, setSavingEdit] = useState(false);

  const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

  // =========================================================
  // Get user information
  // =========================================================

  const getUserInfo = useCallback(() => {
    const token =
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      '';

    const userLabId = localStorage.getItem('userLabId') || '';

    return {
      token,
      userLabId
    };
  }, []);

  // =========================================================
  // Get current user lab ID
  // =========================================================

  useEffect(() => {
    const { userLabId } = getUserInfo();
    setCurrentUserLabId(userLabId);
  }, [getUserInfo]);

  // =========================================================
  // Fetch invoice when invoice number changes
  // =========================================================

  useEffect(() => {
    if (invoiceNumber) {
      fetchInvoiceDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber]);

  // =========================================================
  // Determine source/destination lab
  // =========================================================

  useEffect(() => {
    if (invoiceData && currentUserLabId) {
      const sourceLabId = invoiceData.sourceLab?.userLabId;
      const destLabId = invoiceData.destinationLab?.userLabId;

      setIsSourceLab(
        String(currentUserLabId) === String(sourceLabId)
      );

      setIsDestinationLab(
        String(currentUserLabId) === String(destLabId)
      );
    }
  }, [invoiceData, currentUserLabId]);

  // =========================================================
  // Back navigation
  // =========================================================

  const handleBackNavigation = () => {
    if (isDestinationLab) {
      navigate('/outsource-received');
    } else if (isSourceLab) {
      navigate('/toOutsource');
    } else {
      navigate('/toOutsource');
    }
  };

  // =========================================================
  // Navigate after delete
  // =========================================================

  const handleNavigateAfterDelete = () => {
    if (isDestinationLab) {
      navigate('/outsource-received');
    } else if (isSourceLab) {
      navigate('/toOutsource');
    } else {
      navigate('/toOutsource');
    }
  };

  // =========================================================
  // Currency
  // =========================================================

  const getCurrencySymbol = () => {
    return currency?.currencySymbol || '₮';
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return '0.00';
    }

    const numericAmount =
      typeof amount === 'string'
        ? parseFloat(amount)
        : amount;

    if (isNaN(numericAmount)) {
      return '0.00';
    }

    if (formatAmount) {
      return formatAmount(numericAmount);
    }

    return `${getCurrencySymbol()}${numericAmount.toFixed(2)}`;
  };

  // =========================================================
  // Fetch Invoice Details
  // =========================================================

  const fetchInvoiceDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const { token } = getUserInfo();

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-invoice/by-number/${invoiceNumber}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        const data = response.data.data;

        // Calculate unit price per line (for display purposes only)
        if (
          data.invoiceLines &&
          data.invoiceLines.length > 0
        ) {
          data.invoiceLines = data.invoiceLines.map((line) => {
            const qty = parseFloat(line.units) || 1;
            const totalAmount = parseFloat(line.amount) || 0;

            const unitPrice =
              qty > 0 ? totalAmount / qty : 0;

            return {
              ...line,
              _unitPrice: unitPrice
            };
          });
        }

        // =========================================================
        // IMPORTANT: Do NOT overwrite backend's authoritative totals.
        // The backend now maintains:
        //   - invoiceData.subtotal
        //   - invoiceData.taxes
        //   - invoiceData.total  (subtotal + taxes + shipping)
        //   - invoiceData.paidAmount
        //   - invoiceData.remainingAmount
        //   - invoiceData.invoiceStatus
        //
        // We only compute a fallback subtotal if the backend didn't
        // provide one (legacy invoices).
        // =========================================================
        let safeSubtotal =
          parseFloat(data.invoiceData?.subtotal) || 0;

        if (!safeSubtotal && data.invoiceLines) {
          safeSubtotal = data.invoiceLines.reduce(
            (sum, line) =>
              sum + (parseFloat(line.amount) || 0),
            0
          );
        }

        const safeTaxes =
          parseFloat(data.invoiceData?.taxes) || 0;

        const shippingCost =
          parseFloat(
            data.closeCaseData?.shippingCost
          ) || 0;

        // Prefer backend's total; fall back to subtotal + taxes + shipping
        const backendTotal =
          parseFloat(data.invoiceData?.total);

        const safeTotal = !isNaN(backendTotal)
          ? backendTotal
          : safeSubtotal + safeTaxes + shippingCost;

        const safePaid =
          parseFloat(data.invoiceData?.paidAmount) || 0;

        // Always recompute remaining to be safe
        const safeRemaining = Math.max(
          0,
          safeTotal - safePaid
        );

        // Hydrate invoiceData with safe, non-destructive values
        data.invoiceData = {
          ...data.invoiceData,
          subtotal: safeSubtotal,
          taxes: safeTaxes,
          total: safeTotal,
          paidAmount: safePaid,
          remainingAmount: safeRemaining
        };

        setInvoiceData(data);

        setEditFormData({
          invoiceStatus:
            data.invoiceData?.invoiceStatus || 'unpaid',

          invoiceMessage:
            data.invoiceData?.invoiceMessage || '',

          subtotal: safeSubtotal,

          taxes: safeTaxes,

          total: safeTotal,

          invoiceLines: (data.invoiceLines || []).map(
            (line) => ({
              id: line.id || Date.now().toString(),
              _id: line._id || undefined,
              description: line.description || '',
              units: line.units || '1',
              amount: parseFloat(line.amount) || 0,
              unitPrice:
                line._unitPrice ||
                parseFloat(line.amount) /
                  (parseFloat(line.units) || 1)
            })
          )
        });
      } else {
        setError('Failed to fetch invoice details');
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);

      setError(
        err.response?.data?.message ||
          'Failed to fetch invoice details'
      );

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load invoice details',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // Format Date
  // =========================================================

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'N/A';
    }

    const date = new Date(dateString);

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // =========================================================
  // Status Badge
  // =========================================================

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: {
        text: 'Paid',
        className: 'paid'
      },

      unpaid: {
        text: 'Unpaid',
        className: 'unpaid'
      },

      partial: {
        text: 'Partial',
        className: 'partial'
      },

      partially_paid: {
        text: 'Partially Paid',
        className: 'partial'
      },

      pending: {
        text: 'Pending',
        className: 'unpaid'
      },

      overdue: {
        text: 'Overdue',
        className: 'cancelled'
      },

      cancelled: {
        text: 'Cancelled',
        className: 'cancelled'
      }
    };

    const config =
      statusConfig[status] || {
        text: status,
        className: ''
      };

    return (
      <span
        className={`outsource-status-badge ${config.className}`}
      >
        {config.text}
      </span>
    );
  };

  // =========================================================
  // Print
  // =========================================================

  const handlePrint = () => {
    window.print();
  };

  // =========================================================
  // Download PDF
  // =========================================================

  const handleDownloadPDF = () => {
    if (!invoiceData) {
      Swal.fire({
        icon: 'error',
        title: 'Cannot Generate PDF',
        text: 'Invoice data is missing.',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });

      return;
    }

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pageWidth =
        pdf.internal.pageSize.getWidth();

      const pageHeight =
        pdf.internal.pageSize.getHeight();

      const margin = {
        top: 15,
        right: 15,
        bottom: 20,
        left: 15
      };

      const usableWidth =
        pageWidth - margin.left - margin.right;

      let currentY = margin.top;

      const addText = (text, x, y, options = {}) => {
        pdf.setFontSize(options.fontSize || 10);

        pdf.setFont(
          'helvetica',
          options.style || 'normal'
        );

        pdf.setTextColor(options.color || '#333333');

        const textToPrint = String(text ?? '');

        const textLines = pdf.splitTextToSize(
          textToPrint,
          options.maxWidth || usableWidth
        );

        pdf.text(textLines, x, y, {
          align: options.align || 'left'
        });

        return (
          y +
          textLines.length *
            (options.lineHeight || 5)
        );
      };

      addText(
        'OUTSOURCE INVOICE',
        margin.left,
        currentY,
        {
          fontSize: 18,
          style: 'bold',
          color: '#004aad',
          lineHeight: 9
        }
      );

      let headerRightY = currentY;

      headerRightY = addText(
        `Invoice #${invoiceData.invoiceNumber || 'N/A'}`,
        pageWidth - margin.right,
        headerRightY,
        {
          align: 'right',
          fontSize: 14,
          style: 'bold',
          lineHeight: 7
        }
      );

      addText(
        `Date: ${formatDate(
          invoiceData.invoiceData?.invoiceDate
        )}`,
        pageWidth - margin.right,
        headerRightY,
        {
          align: 'right',
          fontSize: 8,
          color: '#666666',
          lineHeight: 4
        }
      );

      currentY += 15;

      // Lab boxes
      pdf.setFillColor('#eaf0ff');

      pdf.rect(
        margin.left,
        currentY,
        usableWidth / 2 - 5,
        35,
        'F'
      );

      pdf.rect(
        margin.left + usableWidth / 2 + 5,
        currentY,
        usableWidth / 2 - 5,
        35,
        'F'
      );

      addText(
        'From (Source Lab)',
        margin.left + 3,
        currentY + 6,
        {
          fontSize: 9,
          style: 'bold',
          color: '#004aad',
          lineHeight: 5
        }
      );

      addText(
        `Lab: ${invoiceData.sourceLab?.labName || 'N/A'}`,
        margin.left + 3,
        currentY + 13,
        {
          fontSize: 8,
          lineHeight: 4,
          color: '#555555'
        }
      );

      addText(
        `ID: ${invoiceData.sourceLab?.userLabId || 'N/A'}`,
        margin.left + 3,
        currentY + 19,
        {
          fontSize: 8,
          lineHeight: 4,
          color: '#555555'
        }
      );

      addText(
        `Created By: ${
          invoiceData.createdBy?.labName || 'N/A'
        }`,
        margin.left + 3,
        currentY + 25,
        {
          fontSize: 8,
          lineHeight: 4,
          color: '#555555'
        }
      );

      addText(
        'To (Destination Lab)',
        margin.left + usableWidth / 2 + 8,
        currentY + 6,
        {
          fontSize: 9,
          style: 'bold',
          color: '#004aad',
          lineHeight: 5
        }
      );

      addText(
        `Lab: ${
          invoiceData.destinationLab?.labName || 'N/A'
        }`,
        margin.left + usableWidth / 2 + 8,
        currentY + 13,
        {
          fontSize: 8,
          lineHeight: 4,
          color: '#555555'
        }
      );

      addText(
        `ID: ${
          invoiceData.destinationLab?.userLabId || 'N/A'
        }`,
        margin.left + usableWidth / 2 + 8,
        currentY + 19,
        {
          fontSize: 8,
          lineHeight: 4,
          color: '#555555'
        }
      );

      currentY += 45;

      // Case details
      pdf.setFillColor('#f8f9fa');

      pdf.rect(
        margin.left,
        currentY,
        usableWidth,
        25,
        'F'
      );

      addText(
        `Case #: ${invoiceData.caseNumber || 'N/A'}`,
        margin.left + 3,
        currentY + 6,
        {
          fontSize: 9,
          style: 'bold',
          lineHeight: 5
        }
      );

      addText(
        `Procedure: ${
          invoiceData.procedure?.procedureName || 'N/A'
        }`,
        margin.left + 3,
        currentY + 12,
        {
          fontSize: 8,
          color: '#555555',
          lineHeight: 4
        }
      );

      addText(
        `Pricing: ${
          invoiceData.procedure?.pricingType || 'N/A'
        }`,
        margin.left + 3,
        currentY + 18,
        {
          fontSize: 8,
          color: '#555555',
          lineHeight: 4
        }
      );

      addText(
        `Created: ${formatDate(
          invoiceData.caseCreatedAt
        )}`,
        margin.left + usableWidth / 2 + 3,
        currentY + 6,
        {
          fontSize: 8,
          color: '#555555',
          lineHeight: 4
        }
      );

      addText(
        `Required: ${formatDate(
          invoiceData.caseRequiredDate
        )}`,
        margin.left + usableWidth / 2 + 3,
        currentY + 12,
        {
          fontSize: 8,
          color: '#555555',
          lineHeight: 4
        }
      );

      addText(
        `Status: ${invoiceData.caseStatus || 'N/A'}`,
        margin.left + usableWidth / 2 + 3,
        currentY + 18,
        {
          fontSize: 8,
          color: '#555555',
          lineHeight: 4
        }
      );

      currentY += 35;

      // Invoice lines
      if (
        invoiceData.invoiceLines &&
        invoiceData.invoiceLines.length > 0
      ) {
        pdf.setFillColor('#eaf0ff');

        pdf.rect(
          margin.left,
          currentY,
          usableWidth,
          8,
          'F'
        );

        const colWidths = {
          desc: usableWidth * 0.42,
          qty: usableWidth * 0.15,
          price: usableWidth * 0.18,
          total: usableWidth * 0.25
        };

        let colX = margin.left + 3;

        addText('Description', colX, currentY + 5, {
          fontSize: 8,
          style: 'bold',
          color: '#004aad',
          lineHeight: 4
        });

        colX += colWidths.desc;

        addText(
          'Qty',
          colX + colWidths.qty - 3,
          currentY + 5,
          {
            align: 'right',
            fontSize: 8,
            style: 'bold',
            color: '#004aad',
            lineHeight: 4
          }
        );

        colX += colWidths.qty;

        addText(
          'Unit Price',
          colX + colWidths.price - 3,
          currentY + 5,
          {
            align: 'right',
            fontSize: 8,
            style: 'bold',
            color: '#004aad',
            lineHeight: 4
          }
        );

        colX += colWidths.price;

        addText(
          'Total',
          colX + colWidths.total - 3,
          currentY + 5,
          {
            align: 'right',
            fontSize: 8,
            style: 'bold',
            color: '#004aad',
            lineHeight: 4
          }
        );

        currentY += 9;

        pdf.setDrawColor('#dddddd');
        pdf.setLineWidth(0.2);

        invoiceData.invoiceLines.forEach((line, index) => {
          if (index % 2 !== 0) {
            pdf.setFillColor('#f9f9f9');

            pdf.rect(
              margin.left,
              currentY,
              usableWidth,
              7,
              'F'
            );
          }

          let cellX = margin.left + 3;

          addText(
            line.description || 'N/A',
            cellX,
            currentY + 5,
            {
              maxWidth: colWidths.desc - 6,
              fontSize: 8,
              lineHeight: 4
            }
          );

          cellX += colWidths.desc;

          addText(
            line.units || '1',
            cellX + colWidths.qty - 3,
            currentY + 5,
            {
              align: 'right',
              fontSize: 8,
              lineHeight: 4
            }
          );

          cellX += colWidths.qty;

          const qty = parseFloat(line.units) || 1;

          const totalAmount =
            parseFloat(line.amount) || 0;

          const unitPrice =
            qty > 0 ? totalAmount / qty : 0;

          addText(
            formatCurrency(unitPrice),
            cellX + colWidths.price - 3,
            currentY + 5,
            {
              align: 'right',
              fontSize: 8,
              lineHeight: 4
            }
          );

          cellX += colWidths.price;

          addText(
            formatCurrency(line.amount),
            cellX + colWidths.total - 3,
            currentY + 5,
            {
              align: 'right',
              fontSize: 8,
              lineHeight: 4,
              style: 'bold'
            }
          );

          currentY += 7;
        });
      }

      currentY += 15;

      // Shipping
      if (
        invoiceData.closeCaseData &&
        (invoiceData.closeCaseData.shipper ||
          invoiceData.closeCaseData.trackNumber)
      ) {
        pdf.setFillColor('#f8f9fa');

        pdf.rect(
          margin.left,
          currentY,
          usableWidth,
          20,
          'F'
        );

        addText(
          'Shipping Details',
          margin.left + 3,
          currentY + 6,
          {
            fontSize: 9,
            style: 'bold',
            color: '#004aad',
            lineHeight: 5
          }
        );

        addText(
          `Shipper: ${
            invoiceData.closeCaseData.shipper || 'N/A'
          } | Tracking: ${
            invoiceData.closeCaseData.trackNumber || 'N/A'
          } | Cost: ${formatCurrency(
            invoiceData.closeCaseData.shippingCost
          )}`,
          margin.left + 3,
          currentY + 13,
          {
            fontSize: 8,
            color: '#555555',
            lineHeight: 4
          }
        );

        currentY += 30;
      }

      // =========================================================
      // Totals — use authoritative values from backend
      // =========================================================
      const displaySubtotal =
        parseFloat(invoiceData.invoiceData?.subtotal) || 0;

      const displayTaxes =
        parseFloat(invoiceData.invoiceData?.taxes) || 0;

      const displayShippingCost =
        parseFloat(
          invoiceData.closeCaseData?.shippingCost
        ) || 0;

      const displayTotal =
        parseFloat(invoiceData.invoiceData?.total) ||
        displaySubtotal + displayTaxes + displayShippingCost;

      const displayPaid =
        parseFloat(invoiceData.invoiceData?.paidAmount) || 0;

      const displayRemaining =
        parseFloat(
          invoiceData.invoiceData?.remainingAmount
        ) || Math.max(0, displayTotal - displayPaid);

      const summaryX = margin.left + usableWidth * 0.55;

      addText('Subtotal:', summaryX, currentY, {
        fontSize: 9,
        lineHeight: 5,
        style: 'bold',
        color: '#444444'
      });

      addText(
        formatCurrency(displaySubtotal),
        summaryX + usableWidth * 0.45,
        currentY,
        {
          align: 'right',
          fontSize: 9,
          lineHeight: 5
        }
      );

      currentY += 7;

      addText('Taxes:', summaryX, currentY, {
        fontSize: 9,
        lineHeight: 5,
        style: 'bold',
        color: '#444444'
      });

      addText(
        formatCurrency(displayTaxes),
        summaryX + usableWidth * 0.45,
        currentY,
        {
          align: 'right',
          fontSize: 9,
          lineHeight: 5
        }
      );

      currentY += 7;

      if (displayShippingCost > 0) {
        addText('Shipping:', summaryX, currentY, {
          fontSize: 9,
          lineHeight: 5,
          style: 'bold',
          color: '#444444'
        });

        addText(
          formatCurrency(displayShippingCost),
          summaryX + usableWidth * 0.45,
          currentY,
          {
            align: 'right',
            fontSize: 9,
            lineHeight: 5
          }
        );

        currentY += 7;
      }

      pdf.setDrawColor('#004aad');
      pdf.setLineWidth(0.4);

      pdf.line(
        summaryX,
        currentY,
        margin.left + usableWidth,
        currentY
      );

      currentY += 3;

      pdf.setFillColor('#eaf0ff');

      pdf.rect(
        summaryX,
        currentY - 2,
        usableWidth - (summaryX - margin.left),
        10,
        'F'
      );

      addText('Total:', summaryX + 3, currentY + 5, {
        fontSize: 11,
        style: 'bold',
        color: '#004aad',
        lineHeight: 6
      });

      addText(
        formatCurrency(displayTotal),
        margin.left + usableWidth - 3,
        currentY + 5,
        {
          align: 'right',
          fontSize: 11,
          style: 'bold',
          color: '#004aad',
          lineHeight: 6
        }
      );

      currentY += 15;

      // Paid + Remaining
      addText('Paid:', summaryX, currentY, {
        fontSize: 9,
        lineHeight: 5,
        style: 'bold',
        color: '#2e7d32'
      });

      addText(
        formatCurrency(displayPaid),
        summaryX + usableWidth * 0.45,
        currentY,
        {
          align: 'right',
          fontSize: 9,
          lineHeight: 5,
          color: '#2e7d32'
        }
      );

      currentY += 7;

      addText('Remaining:', summaryX, currentY, {
        fontSize: 9,
        lineHeight: 5,
        style: 'bold',
        color: '#c62828'
      });

      addText(
        formatCurrency(displayRemaining),
        summaryX + usableWidth * 0.45,
        currentY,
        {
          align: 'right',
          fontSize: 9,
          lineHeight: 5,
          color: '#c62828'
        }
      );

      currentY += 12;

      if (invoiceData.invoiceData?.invoiceMessage) {
        addText(
          invoiceData.invoiceData.invoiceMessage,
          margin.left,
          currentY,
          {
            fontSize: 8,
            color: '#666666',
            lineHeight: 4,
            style: 'italic'
          }
        );

        currentY += 10;
      }

      const footerY = pageHeight - margin.bottom;

      pdf.setDrawColor('#cccccc');
      pdf.setLineWidth(0.2);

      pdf.line(
        margin.left,
        footerY - 8,
        pageWidth - margin.right,
        footerY - 8
      );

      addText(
        'Thank you for your business!',
        pageWidth / 2,
        footerY - 2,
        {
          align: 'center',
          fontSize: 8,
          color: '#4CAF50',
          style: 'italic',
          lineHeight: 4
        }
      );

      pdf.save(
        `Outsource_Invoice_${invoiceData.invoiceNumber}.pdf`
      );

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'PDF downloaded successfully',
        confirmButtonColor: '#004aad',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Error generating PDF:', err);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate PDF',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  // =========================================================
  // Edit
  // =========================================================

  const handleEditClick = () => {
    const calculatedSubtotal =
      parseFloat(invoiceData.invoiceData?.subtotal) ||
      (invoiceData.invoiceLines || []).reduce(
        (sum, line) =>
          sum + (parseFloat(line.amount) || 0),
        0
      );

    const taxes =
      parseFloat(invoiceData.invoiceData?.taxes) || 0;

    setEditFormData({
      invoiceStatus:
        invoiceData.invoiceData?.invoiceStatus || 'unpaid',

      invoiceMessage:
        invoiceData.invoiceData?.invoiceMessage || '',

      subtotal: calculatedSubtotal,

      taxes,

      total:
        parseFloat(invoiceData.invoiceData?.total) ||
        calculatedSubtotal + taxes,

      invoiceLines: (invoiceData.invoiceLines || []).map(
        (line) => {
          const qty = parseFloat(line.units) || 1;

          const totalAmount =
            parseFloat(line.amount) || 0;

          const unitPrice =
            qty > 0 ? totalAmount / qty : 0;

          return {
            id: line.id || Date.now().toString(),
            _id: line._id || undefined,
            description: line.description || '',
            units: line.units || '1',
            amount: totalAmount,
            unitPrice
          };
        }
      )
    });

    setIsEditing(true);
  };

  // =========================================================
  // Cancel Edit
  // =========================================================

  const handleCancelEdit = () => {
    setIsEditing(false);

    const calculatedSubtotal =
      parseFloat(invoiceData.invoiceData?.subtotal) ||
      (invoiceData.invoiceLines || []).reduce(
        (sum, line) =>
          sum + (parseFloat(line.amount) || 0),
        0
      );

    const taxes =
      parseFloat(invoiceData.invoiceData?.taxes) || 0;

    setEditFormData({
      invoiceStatus:
        invoiceData.invoiceData?.invoiceStatus || 'unpaid',

      invoiceMessage:
        invoiceData.invoiceData?.invoiceMessage || '',

      subtotal: calculatedSubtotal,

      taxes,

      total:
        parseFloat(invoiceData.invoiceData?.total) ||
        calculatedSubtotal + taxes,

      invoiceLines: (invoiceData.invoiceLines || []).map(
        (line) => {
          const qty = parseFloat(line.units) || 1;

          const totalAmount =
            parseFloat(line.amount) || 0;

          const unitPrice =
            qty > 0 ? totalAmount / qty : 0;

          return {
            id: line.id || Date.now().toString(),
            _id: line._id || undefined,
            description: line.description || '',
            units: line.units || '1',
            amount: totalAmount,
            unitPrice
          };
        }
      )
    });
  };

  // =========================================================
  // Save Edit
  // =========================================================

  const handleEditSave = async () => {
    setSavingEdit(true);

    try {
      const { token } = getUserInfo();

      const subtotal =
        editFormData.invoiceLines.reduce(
          (sum, line) =>
            sum + (parseFloat(line.amount) || 0),
          0
        );

      const updatedInvoiceLines =
        editFormData.invoiceLines.map((line) => ({
          id: line.id || Date.now().toString(),
          _id: line._id || undefined,
          description: line.description,
          units: line.units,
          amount: parseFloat(line.amount) || 0
        }));

      const taxes =
        parseFloat(editFormData.taxes) || 0;

      const shippingCost =
        parseFloat(
          invoiceData.closeCaseData?.shippingCost
        ) || 0;

      const updatedData = {
        invoiceLines: updatedInvoiceLines,

        invoiceData: {
          invoiceStatus: editFormData.invoiceStatus,
          invoiceMessage: editFormData.invoiceMessage,
          subtotal,
          taxes,
          total: subtotal + taxes + shippingCost,
          invoiceDate:
            invoiceData.invoiceData?.invoiceDate
        }
      };

      const response = await axios.put(
        `${API_BASE_URL}/api/outsource-invoice/update/${invoiceNumber}`,
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Invoice updated successfully',
          confirmButtonColor: '#004aad',
          timer: 2000,
          showConfirmButton: false
        });

        setIsEditing(false);

        fetchInvoiceDetails();
      }
    } catch (err) {
      console.error('Error updating invoice:', err);

      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text:
          err.response?.data?.message ||
          'Failed to update invoice',

        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    } finally {
      setSavingEdit(false);
    }
  };

  // =========================================================
  // Edit Line Change
  // =========================================================

  const handleEditLineChange = (index, field, value) => {
    const newLines = [...editFormData.invoiceLines];

    newLines[index] = {
      ...newLines[index],
      [field]: value
    };

    if (field === 'unitPrice' || field === 'units') {
      const unitPrice =
        parseFloat(newLines[index].unitPrice) || 0;

      const qty = parseFloat(newLines[index].units) || 1;

      newLines[index].amount = unitPrice * qty;
    }

    const subtotal = newLines.reduce(
      (sum, line) =>
        sum + (parseFloat(line.amount) || 0),
      0
    );

    setEditFormData({
      ...editFormData,

      invoiceLines: newLines,

      subtotal,

      total:
        subtotal + (parseFloat(editFormData.taxes) || 0)
    });
  };

  // =========================================================
  // Add Line
  // =========================================================

  const handleAddLine = () => {
    setEditFormData({
      ...editFormData,

      invoiceLines: [
        ...editFormData.invoiceLines,

        {
          id: Date.now().toString(),
          _id: undefined,
          description: '',
          units: '1',
          amount: 0,
          unitPrice: 0
        }
      ]
    });
  };

  // =========================================================
  // Remove Line
  // =========================================================

  const handleRemoveLine = (index) => {
    const newLines = editFormData.invoiceLines.filter(
      (_, i) => i !== index
    );

    const subtotal = newLines.reduce(
      (sum, line) =>
        sum + (parseFloat(line.amount) || 0),
      0    );

    setEditFormData({
      ...editFormData,

      invoiceLines: newLines,

      subtotal,

      total:
        subtotal + (parseFloat(editFormData.taxes) || 0)
    });
  };

  // =========================================================
  // Delete
  // =========================================================

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const { token } = getUserInfo();

        await axios.delete(
          `${API_BASE_URL}/api/outsource-invoice/delete/${invoiceNumber}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            withCredentials: true
          }
        );

        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Invoice has been deleted successfully.',
          confirmButtonColor: '#004aad',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          handleNavigateAfterDelete();
        });
      } catch (err) {
        console.error('Error deleting invoice:', err);

        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text:
            err.response?.data?.message ||
            'Failed to delete invoice',
          confirmButtonColor: '#d33',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  // =========================================================
  // View Case
  // =========================================================

  const handleViewCase = () => {
    if (invoiceData?.caseNumber) {
      navigate(`/outsource-case/${invoiceData.caseNumber}`);
    }
  };

  // =========================================================
  // Dropdown Options
  // =========================================================

  const getDropdownOptions = () => {
    if (isSourceLab) {
      return [
        {
          label: 'Print',
          action: 'Print',
          icon: <FaPrint />
        },

        {
          label: 'Save as PDF',
          action: 'PDF',
          icon: <FaDownload />
        }
      ];
    }

    if (isDestinationLab) {
      return [
        {
          label: 'Edit',
          action: 'Edit',
          icon: <FaEdit />
        },

        {
          label: 'Delete',
          action: 'Delete',
          icon: <FaTrash />
        },

        {
          label: 'Print',
          action: 'Print',
          icon: <FaPrint />
        },

        {
          label: 'Save as PDF',
          action: 'PDF',
          icon: <FaDownload />
        }
      ];
    }

    return [];
  };

  // =========================================================
  // Dropdown Action
  // =========================================================

  const handleOptionClick = (action) => {
    setDropdownOpen(false);

    switch (action) {
      case 'Print':
        handlePrint();
        break;

      case 'PDF':
        handleDownloadPDF();
        break;

      case 'Edit':
        handleEditClick();
        break;

      case 'Delete':
        handleDelete();
        break;

      default:
        break;
    }
  };

  // =========================================================
  // Display Subtotal
  // =========================================================

  const getDisplaySubtotal = () => {
    if (
      invoiceData?.invoiceData?.subtotal !== undefined &&
      invoiceData?.invoiceData?.subtotal !== null
    ) {
      return parseFloat(invoiceData.invoiceData.subtotal) || 0;
    }

    if (!invoiceData?.invoiceLines) {
      return 0;
    }

    return invoiceData.invoiceLines.reduce(
      (sum, line) => sum + (parseFloat(line.amount) || 0),
      0
    );
  };

  // =========================================================
  // Loading
  // =========================================================

  if (loading) {
    return (
      <>
        <Header />
        <Navvbar />

        <div className="outsource-loading">
          <Spinner animation="border" style={{ color: '#004aad' }} />
          <p>Loading invoice details...</p>
        </div>

        <Footer />
      </>
    );
  }

  // =========================================================
  // Error
  // =========================================================

  if (error || !invoiceData) {
    return (
      <>
        <Header />
        <Navvbar />

        <div className="outsource-error-box">
          <p>Error: {error || 'Invoice not found'}</p>

          <Button
            variant="outline-danger"
            onClick={handleBackNavigation}
          >
            Go Back
          </Button>
        </div>

        <Footer />
      </>
    );
  }

  const dropdownOptions = getDropdownOptions();

  const displaySubtotal = getDisplaySubtotal();

  const displayTaxes =
    parseFloat(invoiceData.invoiceData?.taxes) || 0;

  const displayShippingCost =
    parseFloat(invoiceData.closeCaseData?.shippingCost) || 0;

  // Use backend's authoritative total when available
  const backendTotal = parseFloat(invoiceData.invoiceData?.total);
  const displayTotal = !isNaN(backendTotal)
    ? backendTotal
    : displaySubtotal + displayTaxes + displayShippingCost;

  const displayPaid =
    parseFloat(invoiceData.invoiceData?.paidAmount) || 0;

  // Always recompute remaining for safety
  const displayRemaining = Math.max(
    0,
    displayTotal - displayPaid
  );

  // =========================================================
  // Main UI
  // =========================================================

  return (
    <>
      <Header />
      <Navvbar />

      <div className="outsource-invoice-container">
        {/* ===================================================
            Top Bar
            =================================================== */}
        <div className="outsource-top-bar outsource-no-print">
          <button
            className="outsource-back-btn"
            onClick={handleBackNavigation}
          >
            <FaArrowLeft />
            {isDestinationLab
              ? 'Back to Outsource Received'
              : 'Back to Outsource Cases'}
          </button>

          <div className="outsource-action-buttons">
            {isEditing && (
              <>
                <button
                  className="outsource-btn outsource-btn-cancel"
                  onClick={handleCancelEdit}
                >
                  <FaTimes />
                  Cancel
                </button>

                <button
                  className="outsource-btn outsource-btn-save"
                  onClick={handleEditSave}
                  disabled={savingEdit}
                >
                  <FaSave />
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}

            {!isEditing && (
              <>
                {dropdownOptions.length > 0 && (
                  <div className="outsource-dropdown-wrapper">
                    <button
                      className="outsource-btn outsource-btn-actions"
                      onClick={() =>
                        setDropdownOpen(!isDropdownOpen)
                      }
                    >
                      Actions ▾
                    </button>

                    {isDropdownOpen && (
                      <div className="outsource-dropdown-menu">
                        {dropdownOptions.map(
                          (option, index) => (
                            <div
                              key={option.action}
                              className={`outsource-dropdown-item ${
                                option.action === 'Delete'
                                  ? 'outsource-dropdown-item-danger'
                                  : ''
                              } ${
                                index <
                                dropdownOptions.length - 1
                                  ? 'outsource-dropdown-item-border'
                                  : ''
                              }`}
                              onClick={() =>
                                handleOptionClick(option.action)
                              }
                            >
                              {option.icon && (
                                <span>{option.icon}</span>
                              )}
                              {option.label}
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ===================================================
            Invoice Card
            =================================================== */}
        <div className="outsource-invoice-print-area outsource-invoice-card">
          {/* Invoice Header */}
          <div className="outsource-invoice-header">
            <div>
              <h2 className="outsource-invoice-title">
                OUTSOURCE INVOICE
              </h2>

              <p className="outsource-invoice-meta">
                Invoice #:{' '}
                <strong>{invoiceData.invoiceNumber}</strong>
              </p>

              <p className="outsource-invoice-meta">
                Date:{' '}
                <strong>
                  {formatDate(invoiceData.invoiceData?.invoiceDate)}
                </strong>
              </p>

              <div className="outsource-status-wrapper">
                {isEditing ? (
                  <select
                    className="outsource-select"
                    value={editFormData.invoiceStatus}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        invoiceStatus: e.target.value
                      })
                    }
                  >
                    <option value="unpaid">Unpaid</option>
                    <option value="partially_paid">
                      Partially Paid
                    </option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="overdue">Overdue</option>
                  </select>
                ) : (
                  getStatusBadge(
                    invoiceData.invoiceData?.invoiceStatus
                  )
                )}
              </div>
            </div>

            <div className="outsource-header-right">
              <h4 className="outsource-case-details-title">
                Case Details
              </h4>

              <p className="outsource-invoice-meta">
                Case #{' '}
                <span
                  className="outsource-case-link"
                  onClick={handleViewCase}
                  title="Click to view case details"
                >
                  {invoiceData.caseNumber}
                </span>
              </p>

              <p className="outsource-invoice-meta">
                Created:{' '}
                <strong>
                  {formatDate(invoiceData.caseCreatedAt)}
                </strong>
              </p>

              <p className="outsource-invoice-meta">
                Required:{' '}
                <strong>
                  {formatDate(invoiceData.caseRequiredDate)}
                </strong>
              </p>

              <p className="outsource-invoice-meta">
                Status:{' '}
                <strong>
                  {invoiceData.caseStatus?.replace('_', ' ') || 'N/A'}
                </strong>
              </p>
            </div>
          </div>

          {/* Lab Information */}
          <div className="outsource-lab-info-grid">
            <div className="outsource-lab-info-box">
              <h5 className="outsource-lab-info-title">
                From (Source Lab)
              </h5>

              <p className="outsource-lab-info-row">
                <strong>Lab Name:</strong>{' '}
                <span>
                  {invoiceData.sourceLab?.labName || 'N/A'}
                </span>
              </p>

              <p className="outsource-lab-info-row">
                <strong>Created By:</strong>{' '}
                <span>
                  {invoiceData.createdBy?.labName || 'N/A'}
                </span>
              </p>
            </div>

            <div className="outsource-lab-info-box">
              <h5 className="outsource-lab-info-title">
                To (Destination Lab)
              </h5>

              <p className="outsource-lab-info-row">
                <strong>Lab Name:</strong>{' '}
                <span>
                  {invoiceData.destinationLab?.labName || 'N/A'}
                </span>
              </p>
            </div>
          </div>

          {/* Procedure Details */}
          <div className="outsource-procedure-details"></div>

          {/* =================================================
              Edit Mode
              ================================================= */}
          {isEditing ? (
            <div className="outsource-edit-section">
              <div className="outsource-edit-header">
                <h5 className="outsource-edit-title">
                  Invoice Items
                </h5>

                <button
                  className="outsource-add-line-btn"
                  onClick={handleAddLine}
                >
                  <FaPlus />
                  Add Line
                </button>
              </div>

              {editFormData.invoiceLines.map((line, index) => (
                <div
                  key={line.id || index}
                  className={`outsource-edit-line ${
                    index % 2 === 0
                      ? 'outsource-edit-line-even'
                      : 'outsource-edit-line-odd'
                  }`}
                >
                  <input
                    className="outsource-input"
                    placeholder="Description"
                    value={line.description}
                    onChange={(e) =>
                      handleEditLineChange(
                        index,
                        'description',
                        e.target.value
                      )
                    }
                  />

                  <input
                    className="outsource-input"
                    placeholder="Quantity"
                    value={line.units}
                    onChange={(e) =>
                      handleEditLineChange(
                        index,
                        'units',
                        e.target.value
                      )
                    }
                  />

                  <input
                    className="outsource-input"
                    type="number"
                    placeholder="Total Amount"
                    value={line.amount}
                    onChange={(e) =>
                      handleEditLineChange(
                        index,
                        'amount',
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />

                  <button
                    className="outsource-remove-line-btn"
                    onClick={() => handleRemoveLine(index)}
                    title="Remove line"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}

              {editFormData.invoiceLines.length === 0 && (
                <div className="outsource-empty-lines">
                  No items added. Click "Add Line" to add invoice
                  items.
                </div>
              )}

              <div className="outsource-message-group">
                <label className="outsource-message-label">
                  Invoice Message
                </label>

                <textarea
                  className="outsource-textarea"
                  value={editFormData.invoiceMessage}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      invoiceMessage: e.target.value
                    })
                  }
                  rows="2"
                  placeholder="Thank you for your business!"
                />
              </div>
            </div>
          ) : (
            <>
              {invoiceData.invoiceLines &&
                invoiceData.invoiceLines.length > 0 && (
                  <div className="outsource-lines-section">
                    <h5 className="outsource-lines-title">
                      Invoice Items
                    </h5>

                    <div className="outsource-table-wrapper">
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th style={{ width: '50px' }}>#</th>
                            <th>Description</th>
                            <th
                              className="outsource-cell-right"
                              style={{ width: '100px' }}
                            >
                              Quantity
                            </th>
                            <th
                              className="outsource-cell-right"
                              style={{ width: '150px' }}
                            >
                              Total
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {invoiceData.invoiceLines.map(
                            (line, index) => (
                              <tr
                                key={
                                  line._id ||
                                  line.id ||
                                  index
                                }
                              >
                                <td>{index + 1}</td>
                                <td>{line.description || 'N/A'}</td>
                                <td className="outsource-cell-right">
                                  {line.units || '1'}
                                </td>
                                <td className="outsource-cell-amount">
                                  {formatCurrency(line.amount)}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
            </>
          )}

          {/* =================================================
              Totals
              ================================================= */}
          <div className="outsource-totals-wrapper">
            <div className="outsource-totals-section">
              <div className="outsource-total-row">
                <strong>Subtotal:</strong>
                <span>
                  {isEditing
                    ? formatCurrency(editFormData.subtotal)
                    : formatCurrency(displaySubtotal)}
                </span>
              </div>

              <div className="outsource-total-row">
                <strong>Taxes:</strong>
                {isEditing ? (
                  <input
                    className="outsource-input outsource-input-tax"
                    type="number"
                    value={editFormData.taxes}
                    onChange={(e) => {
                      const taxes =
                        parseFloat(e.target.value) || 0;

                      setEditFormData({
                        ...editFormData,
                        taxes,
                        total: editFormData.subtotal + taxes
                      });
                    }}
                  />
                ) : (
                  <span>{formatCurrency(displayTaxes)}</span>
                )}
              </div>

              {displayShippingCost > 0 && (
                <div className="outsource-total-row">
                  <strong>Shipping:</strong>
                  <span>{formatCurrency(displayShippingCost)}</span>
                </div>
              )}

              <div className="outsource-total-final">
                <strong>Total:</strong>
                <span>
                  {isEditing
                    ? formatCurrency(editFormData.total)
                    : formatCurrency(displayTotal)}
                </span>
              </div>

              {/* =============================================
                  Payment Summary (Paid + Remaining)
                  Only shown when NOT editing and payment exists
                  ============================================= */}
              {!isEditing && (
                <>
                  <div className="outsource-total-row">
                    <strong style={{ color: '#2e7d32' }}>
                      Paid:
                    </strong>
                    <span style={{ color: '#2e7d32' }}>
                      {formatCurrency(displayPaid)}
                    </span>
                  </div>

                  <div className="outsource-total-final">
                    <strong style={{ color: '#c62828' }}>
                      Remaining:
                    </strong>
                    <span style={{ color: '#c62828' }}>
                      {formatCurrency(displayRemaining)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* =================================================
              Shipping Details
              ================================================= */}
          {invoiceData.closeCaseData &&
            (invoiceData.closeCaseData.shipper ||
              invoiceData.closeCaseData.trackNumber) && (
              <div className="outsource-shipping-section">
                <h5 className="outsource-shipping-title">
                  Shipping Details
                </h5>

                <div className="outsource-shipping-grid">
                  <p className="outsource-shipping-row">
                    <strong>Ship Option:</strong>{' '}
                    <span>
                      {invoiceData.closeCaseData.shipOption ||
                        'N/A'}
                    </span>
                  </p>

                  <p className="outsource-shipping-row">
                    <strong>Shipper:</strong>{' '}
                    <span>
                      {invoiceData.closeCaseData.shipper || 'N/A'}
                    </span>
                  </p>

                  <p className="outsource-shipping-row">
                    <strong>Tracking Number:</strong>{' '}
                    <span>
                      {invoiceData.closeCaseData.trackNumber ||
                        'N/A'}
                    </span>
                  </p>

                  <p className="outsource-shipping-row">
                    <strong>Shipping Cost:</strong>{' '}
                    <span className="outsource-shipping-cost">
                      {formatCurrency(
                        invoiceData.closeCaseData.shippingCost
                      )}
                    </span>
                  </p>
                </div>
              </div>
            )}

          {/* Invoice Message */}
          {!isEditing && invoiceData.invoiceData?.invoiceMessage && (
            <div className="outsource-invoice-message">
              <p>{invoiceData.invoiceData.invoiceMessage}</p>
            </div>
          )}

          {/* Case Notes */}
          {invoiceData.caseNotes && (
            <div className="outsource-case-notes">
              <h5 className="outsource-case-notes-title">
                Case Notes
              </h5>

              <p className="outsource-case-notes-text">
                {invoiceData.caseNotes}
              </p>
            </div>
          )}

          {/* Invoice Footer */}
          <div className="outsource-invoice-footer">
            <span className="outsource-footer-thanks">
              Thank you for your business!
            </span>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default OutsourceInvoiceDetail;